import { FeedPost } from 'components/collections/Post'
import {
  Tabs,
  Loading,
  ShareBar,
  Modal,
  Button,
  FollowButton,
  Tag,
  Pagination,
} from 'components/elements'
import { withSizes } from 'components/helpers'
import { getCloudFlareImgVariant } from 'components/helpers/withSizes'
import { useSession } from 'context/Session'
import { format } from 'date-fns'
import Link from 'next/link'
import { useRouter } from 'next/router'
import NProgress from 'nprogress'
import * as R from 'ramda'
import { useState, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'

import {
  loadArticles,
  deleteDraft,
  deleteArticle,
  loadFollowing,
  loadFollowers,
  loadPosts,
  followUser,
  unfollowUser,
  followTopic,
  unfollowTopic,
} from '../modules/datasource'

export const Container = styled.div`
  background: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  padding: 0 152px;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
  flex: 1;
  ${({ theme }) => theme.media.tablet} {
    padding: 0 32px;
  }
  ${({ theme }) => theme.media.phone} {
    padding: 0;
  }
`

export const Content = styled.div`
  flex: 1;
`

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
`
const TabsContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  ${({ theme }) => theme.media.phone} {
    padding: 32px 16px;
  }
`

const ArticleContainer = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  & + & {
    margin-top: 32px;
  }

  ${({ theme }) => theme.media.phone} {
    flex-direction: column;
  }
`
const ArticleBanner = styled.div`
  height: 153px;
  min-width: 272px;
  max-width: 272px;
  border-radius: 0px;
  background-color: ${({ theme }) => theme.colors.grey};
  margin-right: 16px;
  position: relative;
  cursor: pointer;

  ${({ theme }) => theme.media.phone} {
    max-width: unset;
    width: 100%;
    height: 193px;
    margin-bottom: 24px;
  }
`
const ArticleInfo = styled.div`
  display: flex;
  flex-direction: column;
`
const ArticleTitle = styled.div`
  font-size: 22px;
  margin-bottom: 16px;
  cursor: pointer;
`
const ArticleDate = styled.div`
  margin-bottom: 32px;
  flex: 1;
  color: ${({ theme }) => theme.colors.grey};
`
const ShareWrapper = styled.div`
  justify-self: flex-end;
`
export const ImgProfile = styled.div`
  height: 100%;
  width: 100%;
  ${({ src }) =>
    src &&
    css`
      background-image: ${`url(${src})`};
      background-size: cover;
      background-position: center;
    `};
`
const DeleteContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 416px;
  margin: 0 auto;
  height: 100%;
  min-height: 300px;
  max-height: 100vh;
  ${({ theme }) => theme.media.phone} {
    padding: 0 15px;
  }
`
const DeleteTitle = styled.div`
  ${({ theme }) => theme.font.h3}
  margin-bottom: 32px;
  text-align: center;
  max-width: 478px;

  ${({ theme }) => theme.media.phone} {
    br {
      display: none;
    }
  }
`
const DeleteButton = styled(Button)`
  & + & {
    margin-top: 8px;
  }
`
const StyledLink = styled(Link)`
  text-decoration: unset;
  color: ${({ theme }) => theme.colors.black};
`

const Article = ({
  id,
  title,
  publishedAt,
  image,
  isPublished,
  accessToken,
  setListState,
  slug,
  host,
}) => {
  const shareLink = `https://${host}/a/${slug}`
  const router = useRouter()
  const path = isPublished ? `/a/${slug}` : `/drafts/${id}`
  const [isOpen, setOpen] = useState(null)

  return (
    <ArticleContainer>
      <Link href={path} passHref>
        <ArticleBanner>
          {image && (
            <ImgProfile
              data-test="icon"
              src={getCloudFlareImgVariant(image, {
                default: 'articleImageSmall',
                phone: 'mobile',
              })}
              alt="article"
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              quality={1}
              priority
              loading="eager"
            />
          )}
        </ArticleBanner>
      </Link>
      <ArticleInfo>
        <StyledLink href={path} target="_blank" rel="noreferrer">
          <ArticleTitle>{title || 'Draft'}</ArticleTitle>
        </StyledLink>
        <ArticleDate>
          {publishedAt &&
            `Published ${format(new Date(publishedAt), 'MMMM d, yyyy')}`}
        </ArticleDate>
        <ShareWrapper>
          <ShareBar
            id={id}
            disableShare={!isPublished}
            url={shareLink}
            quote={title}
            onEdit={() => router.push(`/articles/${id}/edit`)}
            actions={[
              {
                label: 'Delete',
                onClick: () => setOpen(isPublished ? 'article' : 'draft'),
              },
            ]}
          />
        </ShareWrapper>
      </ArticleInfo>

      <Modal
        isOpen={!!isOpen}
        close={() => setOpen(null)}
        closeOnOutside={false}
      >
        <DeleteContent>
          <DeleteTitle>
            Are you sure you want to delete
            <br /> your {isOpen}?
          </DeleteTitle>
          <DeleteButton
            type="button"
            onClick={async () => {
              if (isOpen === 'draft') {
                await deleteDraft({ accessToken, id })
              } else {
                await deleteArticle({ accessToken, id })
              }
              setListState(({ list, total }) => ({
                list: R.reject(R.propEq(id, 'id'), list),
                total: total - 1,
              }))
              setOpen(null)
            }}
          >
            Yes, delete this {isOpen}
          </DeleteButton>
          <DeleteButton
            type="button"
            variant="text"
            onClick={() => setOpen(null)}
          >
            Cancel
          </DeleteButton>
        </DeleteContent>
      </Modal>
    </ArticleContainer>
  )
}

const EmptyContainer = styled.div`
  color: ${({ theme }) => theme.colors.grey};
`
const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ theme }) => theme.media.tablet} {
    min-width: 65vw;
  }
`
const ArticleList = ({ accessToken, host, tab, isMobile }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [listState, setListState] = useState([])
  const [crrPage, setPage] = useState(1)
  const { list, total } = listState
  const isPublished = tab === 'published'

  const changePage = page => {
    window?.scroll(0, 0)
    setPage(page)
  }

  useEffect(() => {
    setIsLoading(true)
    loadArticles({
      accessToken,
      type: tab,
    }).then(resp => {
      setListState(resp)
      setIsLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const config = {
    host,
    accessToken,
    setListState,
  }

  return (
    <ListContainer>
      {isLoading ? (
        <Loading style={{ margin: '10px auto' }} />
      ) : !R.isEmpty(list) ? (
        list?.map((item, i) => (
          <Article
            {...item}
            key={`article-${i}`}
            {...config}
            isPublished={isPublished}
          />
        ))
      ) : (
        <EmptyContainer>
          {isPublished ? 'There are no articles published.' : 'No drafts'}
        </EmptyContainer>
      )}
      <Pagination
        style={{ marginTop: 32 }}
        count={Math.ceil(total / 20)}
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        page={crrPage}
        onChange={(_, v) => {
          changePage(v)
        }}
      />
    </ListContainer>
  )
}
const StyledOther = styled.div`
  display: flex;
  width: 100%;
  max-width: 416px;
  & + & {
    margin-top: 16px;
  }
`
const AuthorImg = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryL};
  border-radius: 50%;
  height: 30px;
  width: 30px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`
const OtherImg = styled(AuthorImg)`
  cursor: pointer;
  height: 48px;
  width: 48px;
  min-height: 48px;
  min-width: 48px;
  text-transform: uppercase;
`
const OtherDesc = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`
const OtherSingleLine = styled.div`
  color: ${({ theme }) => theme.colors.grey};
  ${({ theme }) => theme.font.body2}
`
const StyledImg = styled.img`
  height: 100%;
  width: 100%;
  object-position: center;
  object-fit: cover;
`
const StyledName = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
`
const Person = ({
  username,
  profileImage,
  singleLineStatement,
  accessToken,
  disableFollow,
}) => {
  const [isFollowing, setIsFollowing] = useState(true)
  const authorPath = `/p/${username}`

  const toggleFollow = async () => {
    NProgress.start()
    if (isFollowing) {
      await unfollowUser({ accessToken, username })
    } else {
      await followUser({ accessToken, username })
    }
    setIsFollowing(R.not)
    NProgress.done()
  }

  return !username ? null : (
    <StyledOther>
      <Link href={authorPath} passHref>
        <OtherImg>
          {profileImage ? (
            <StyledImg
              src={getCloudFlareImgVariant(profileImage, {
                default: 'profileSmall',
              })}
            />
          ) : (
            R.head(username || '')
          )}
        </OtherImg>
      </Link>
      <OtherDesc>
        <StyledName>
          <Link href={authorPath}>{username}</Link>
          {!disableFollow && (
            <FollowButton
              isFollowing={isFollowing}
              style={{ marginLeft: 'auto' }}
              onClick={toggleFollow}
            />
          )}
        </StyledName>
        <OtherSingleLine>{singleLineStatement}</OtherSingleLine>
      </OtherDesc>
    </StyledOther>
  )
}

const TopicContainer = styled(StyledOther)`
  display: flex;
  align-items: center;
`
const TagItem = styled(Tag)`
  margin: 4px;
  padding: 12px;
  cursor: pointer;
`

const Topic = ({ slug, name, accessToken }) => {
  const [isFollowing, setIsFollowing] = useState(true)

  const toggleFollow = async () => {
    NProgress.start()
    if (isFollowing) {
      await unfollowTopic({ accessToken, slug })
    } else {
      await followTopic({ accessToken, slug })
    }
    setIsFollowing(R.not)
    NProgress.done()
  }

  return (
    <TopicContainer>
      <Link href={`/topics/${slug}`} passHref>
        <TagItem label={name} />
      </Link>
      <FollowButton
        isFollowing={isFollowing}
        style={{ marginLeft: 'auto' }}
        onClick={toggleFollow}
      />
    </TopicContainer>
  )
}
const FollowingContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
  ${({ theme }) => theme.media.phone} {
    grid-template-columns: 1fr;
  }
`
const StyledFollowingList = styled.div`
  display: flex;
  flex-direction: column;
`
const StyledFollowingListTitle = styled.div`
  ${({ theme }) => theme.font.h3}
  margin-bottom: 24px;
  display: flex;
  align-items: center;
`

const SeeMoreBtn = styled(Link)`
  ${({ theme }) => theme.font.body}
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  transition: all 0.3s;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 8px;
  margin: 0 20px -6px;
  &:hover {
    background: ${({ theme }) => theme.colors.primaryL};
  }
`

const FollowingList = ({ user, accessToken }) => {
  const { profile } = user
  const username = profile?.username
  const [isLoading, setIsLoading] = useState(true)
  const [writerListState, setWriterListState] = useState([])
  const [topicListState, setTopicListState] = useState([])
  const [page] = useState(1)
  const { list: writerList } = writerListState
  const { list: topicList } = topicListState

  useEffect(() => {
    setIsLoading(true)
    username &&
      loadFollowing({ username, page }).then(({ writerState, topicState }) => {
        setWriterListState(({ crr = [] }) => ({
          list: R.concat(crr, writerState?.list || []),
          total: writerState?.total,
        }))
        setTopicListState(({ crr = [] }) => ({
          list: R.concat(crr, topicState?.list || []),
          total: topicState?.total,
        }))
        setIsLoading(false)
      })
  }, [page, username])

  const config = {
    accessToken,
  }

  return (
    <FollowingContainer>
      <StyledFollowingList>
        <StyledFollowingListTitle>
          Your writers
          <SeeMoreBtn href="/search?t=people" prefetch={false}>
            Find more
          </SeeMoreBtn>
        </StyledFollowingListTitle>
        {isLoading ? (
          <Loading style={{ margin: '10px auto' }} />
        ) : !R.isEmpty(writerList) ? (
          writerList?.map((item, i) => (
            <Person {...item} key={`following-p-${i}`} {...config} />
          ))
        ) : (
          <EmptyContainer>Not following anyone</EmptyContainer>
        )}
      </StyledFollowingList>
      <StyledFollowingList>
        <StyledFollowingListTitle>
          Your topics
          <SeeMoreBtn href="/search?t=topics" prefetch={false}>
            Find more
          </SeeMoreBtn>
        </StyledFollowingListTitle>
        {isLoading ? (
          <Loading style={{ margin: '10px auto' }} />
        ) : !R.isEmpty(topicList) ? (
          topicList?.map((item, i) => (
            <Topic {...item} key={`following-t-${i}`} {...config} />
          ))
        ) : (
          <EmptyContainer>Not following any topic</EmptyContainer>
        )}
      </StyledFollowingList>
    </FollowingContainer>
  )
}

const FollowerList = ({ user }) => {
  const { profile } = user
  const username = profile?.username
  const [isLoading, setIsLoading] = useState(true)
  const [listState, setListState] = useState([])
  const [page] = useState(1)
  const { list } = listState

  useEffect(() => {
    setIsLoading(true)
    username &&
      loadFollowers({ username, page }).then(({ list, total }) => {
        setListState(({ crr = [] }) => ({
          list: R.concat(crr, list),
          total,
        }))
        setIsLoading(false)
      })
  }, [page, username])

  return isLoading ? (
    <Loading style={{ margin: '10px auto' }} />
  ) : !R.isEmpty(list) ? (
    list?.map((item, i) => (
      <Person {...item} key={`following-p-${i}`} disableFollow />
    ))
  ) : (
    <EmptyContainer>No one follows</EmptyContainer>
  )
}

const PostList = ({ user, accessToken, host, tab, isMobile, onPostOpen }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [listState, setListState] = useState([])
  const [crrPage, setPage] = useState(1)
  const { list, total } = listState
  const isPublished = tab === 'published'

  const changePage = page => {
    window?.scroll(0, 0)
    setPage(page)
  }

  useEffect(() => {
    if (user?.profile?.username) {
      setIsLoading(true)
      loadPosts({
        username: user?.profile?.username,
        accessToken,
        page: crrPage,
      }).then(resp => {
        setListState(resp)
        setIsLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, crrPage])

  const config = {
    host,
    accessToken,
    setListState,
  }

  return (
    <ListContainer>
      {isLoading ? (
        <Loading style={{ margin: '10px auto' }} />
      ) : !R.isEmpty(list) ? (
        list?.map((item, i) => (
          <FeedPost
            data={item}
            key={`dash-post-${i}`}
            {...config}
            isPublished={isPublished}
            onPostOpen={onPostOpen}
            customPath="/dashboard"
          />
        ))
      ) : (
        <EmptyContainer>
          {isPublished ? 'There are no articles published.' : 'No drafts'}
        </EmptyContainer>
      )}
      <Pagination
        style={{ marginTop: 32 }}
        count={Math.ceil(total / 20)}
        size={isMobile ? 'small' : 'medium'}
        color="primary"
        page={crrPage}
        onChange={(_, v) => {
          changePage(v)
        }}
        setListState={setListState}
      />
    </ListContainer>
  )
}

const listMap = {
  posts: withSizes(PostList),
  published: withSizes(ArticleList),
  draft: withSizes(ArticleList),
  following: FollowingList,
  followers: FollowerList,
}

export const ArticleTabs = ({ host = 'trialsitenews.com', onPostOpen }) => {
  const { user, accessToken } = useSession()
  const [tab, setTab] = useState('published')

  const Component = useMemo(() => listMap[tab] || listMap.published, [tab])

  const config = {
    tab,
    accessToken,
    user,
    host,
    onPostOpen,
  }

  return (
    <TabsContainer>
      <Tabs.TabsHeader style={{ background: 'white' }}>
        <Tabs
          options={[
            { label: 'Published', value: 'published' },
            { label: 'Posts', value: 'posts' },
            { label: 'Draft', value: 'draft' },
            { label: 'Following', value: 'following' },
            { label: 'Followers', value: 'followers' },
          ]}
          defaultValue={tab}
          onChange={setTab}
        />
      </Tabs.TabsHeader>
      <TabsContent>
        <Component {...config} />
      </TabsContent>
    </TabsContainer>
  )
}
